import { useCallback, useEffect, useRef, useState } from "react";
import { ActionIcon, Slider, Text, Tooltip } from "@mantine/core";
import {
  IconCrosshair,
  IconUser,
  IconPointer,
  IconZoomIn,
  IconZoomOut,
  IconRefresh,
  IconSun,
  IconEye,
  IconX,
  IconSquareDashed,
} from "@tabler/icons-react";
import { HEADER_HEIGHT } from "@labeling-tools/out-of-stock/src/config/data";

export const COVERAGE_BETA_SIDEBAR_WIDTH = 240;

type DrawMode = "cursor" | "draw" | "draw-empty" | "draw-user";
type ActiveTool = "cursor" | "zoom" | "brightness" | "eyedrop" | null;

interface Props {
  drawMode: DrawMode;
  onDrawModeChange: (mode: DrawMode) => void;
  brightness: number;
  onBrightnessChange: (value: number) => void;
  boxesVisible: boolean;
  onToggleBoxVisibility: () => void;
  onResetView: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  /** When true, drawing modes (OOS box, empty area, person) are disabled */
  drawingDisabled?: boolean;
  readOnly?: boolean;
}

export const CoverageBetaSidebar = ({
  drawMode,
  onDrawModeChange,
  brightness,
  onBrightnessChange,
  boxesVisible,
  onToggleBoxVisibility,
  onResetView,
  onZoomIn,
  onZoomOut,
  drawingDisabled,
  readOnly,
}: Props) => {
  const [activeTool, setActiveTool] = useState<ActiveTool>("cursor");
  const [showBrightnessSlider, setShowBrightnessSlider] = useState(false);
  const brightnessButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleClassClick = useCallback(
    (mode: DrawMode) => {
      onDrawModeChange(mode);
      setActiveTool(null);
      setShowBrightnessSlider(false);
    },
    [onDrawModeChange],
  );

  const handleToolClick = useCallback(
    (tool: ActiveTool) => {
      if (tool === "brightness") {
        setShowBrightnessSlider((prev) => !prev);
        setActiveTool("brightness");
      } else {
        setShowBrightnessSlider(false);
        setActiveTool(tool);
        if (tool === "cursor") {
          onDrawModeChange("cursor");
        }
        if (tool === "eyedrop") {
          onToggleBoxVisibility();
        }
      }
    },
    [onDrawModeChange, onToggleBoxVisibility],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't intercept when user is typing in an input
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      switch (e.key) {
        // Classes
        case "1":
          if (!drawingDisabled) handleClassClick("draw");
          break;
        case "2":
          if (!drawingDisabled) handleClassClick("draw-empty");
          break;
        case "3":
          if (!drawingDisabled) handleClassClick("draw-user");
          break;
        // Tools
        case "v":
        case "V":
          handleToolClick("cursor");
          break;
        case "=":
        case "+":
          onZoomIn?.();
          break;
        case "-":
          onZoomOut?.();
          break;
        case "0":
          onResetView();
          break;
        case "b":
        case "B":
          handleToolClick("brightness");
          break;
        case "h":
        case "H":
          handleToolClick("eyedrop");
          break;
        default:
          return;
      }
    },
    [
      drawingDisabled,
      handleClassClick,
      handleToolClick,
      onZoomIn,
      onZoomOut,
      onResetView,
    ],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      ref={sidebarRef}
      className="fixed left-0 top-0 bottom-0 flex flex-col bg-white border-r border-gray-200 z-10"
      style={{
        top: readOnly ? HEADER_HEIGHT * 2 : HEADER_HEIGHT,
        width: COVERAGE_BETA_SIDEBAR_WIDTH,
      }}
    >
      {/* Classes (hidden in admin view) */}
      {!readOnly && (
        <>
          <div className="px-2 pt-3 pb-1" data-training="sidebar-classes">
            <Text size="xs" c="dimmed" fw={600} className="text-center mb-2">
              Classes
            </Text>
            <div className="flex flex-col items-center gap-2">
              <Tooltip
                label={
                  drawingDisabled
                    ? "Categorize all detections first"
                    : "Draw OOS Box (1)"
                }
                position="right"
              >
                <ActionIcon
                  size="lg"
                  variant={drawMode === "draw" ? "filled" : "light"}
                  color={drawMode === "draw" ? "blue" : "gray"}
                  onClick={() => handleClassClick("draw")}
                  disabled={drawingDisabled}
                >
                  <IconCrosshair size={20} />
                </ActionIcon>
              </Tooltip>
              <Tooltip
                label={
                  drawingDisabled
                    ? "Categorize all detections first"
                    : "Draw Empty Area (2)"
                }
                position="right"
              >
                <ActionIcon
                  size="lg"
                  variant={drawMode === "draw-empty" ? "filled" : "light"}
                  color={drawMode === "draw-empty" ? "violet" : "gray"}
                  onClick={() => handleClassClick("draw-empty")}
                  disabled={drawingDisabled}
                >
                  <IconSquareDashed size={20} />
                </ActionIcon>
              </Tooltip>
              <Tooltip
                label={
                  drawingDisabled
                    ? "Categorize all detections first"
                    : "Draw Unblurred Person (3)"
                }
                position="right"
              >
                <ActionIcon
                  size="lg"
                  variant={drawMode === "draw-user" ? "filled" : "light"}
                  color={drawMode === "draw-user" ? "orange" : "gray"}
                  onClick={() => handleClassClick("draw-user")}
                  disabled={drawingDisabled}
                >
                  <IconUser size={20} />
                </ActionIcon>
              </Tooltip>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 mx-2 my-2" />
        </>
      )}

      {/* Tools */}
      <div className="px-2 pb-1" data-training="sidebar-tools">
        <Text size="xs" c="dimmed" fw={600} className="text-center mb-2">
          Tools
        </Text>
        <div className="flex flex-col items-center gap-2">
          <Tooltip label="Cursor (V)" position="right">
            <ActionIcon
              size="lg"
              variant={activeTool === "cursor" ? "filled" : "light"}
              color={activeTool === "cursor" ? "blue" : "gray"}
              onClick={() => handleToolClick("cursor")}
            >
              <IconPointer size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Zoom In (+)" position="right">
            <ActionIcon
              size="lg"
              variant="light"
              color="gray"
              onClick={onZoomIn}
            >
              <IconZoomIn size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Zoom Out (−)" position="right">
            <ActionIcon
              size="lg"
              variant="light"
              color="gray"
              onClick={onZoomOut}
            >
              <IconZoomOut size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Reset View (0)" position="right">
            <ActionIcon
              size="lg"
              variant="light"
              color="gray"
              onClick={onResetView}
            >
              <IconRefresh size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Brightness (B)" position="right">
            <ActionIcon
              ref={brightnessButtonRef}
              size="lg"
              variant={activeTool === "brightness" ? "filled" : "light"}
              color={activeTool === "brightness" ? "blue" : "gray"}
              onClick={() => handleToolClick("brightness")}
            >
              <IconSun size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Toggle Box Visibility (H)" position="right">
            <ActionIcon
              size="lg"
              variant="light"
              color={boxesVisible ? "gray" : "red"}
              onClick={() => handleToolClick("eyedrop")}
            >
              <IconEye size={20} />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>

      {/* Brightness slider popup */}
      {showBrightnessSlider &&
        (() => {
          const btnRect = brightnessButtonRef.current?.getBoundingClientRect();
          const sidebarRect = sidebarRef.current?.getBoundingClientRect();
          const topOffset =
            btnRect && sidebarRect ? btnRect.top - sidebarRect.top : 0;
          return (
            <div
              className="absolute bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-20"
              style={{
                left: COVERAGE_BETA_SIDEBAR_WIDTH + 8,
                top: topOffset,
                width: 200,
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <Text size="xs" fw={600}>
                  Brightness
                </Text>
                <div className="flex gap-1">
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    onClick={() => onBrightnessChange(100)}
                    title="Reset brightness"
                  >
                    <IconRefresh size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    onClick={() => setShowBrightnessSlider(false)}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </div>
              </div>
              <Slider
                value={brightness}
                onChange={onBrightnessChange}
                min={20}
                max={200}
                step={5}
                label={(v) => `${v}%`}
                size="sm"
              />
            </div>
          );
        })()}
    </div>
  );
};
